'use client';

import { useState, type ComponentType, type FormEventHandler, type KeyboardEventHandler } from 'react';
import Link from 'next/link';
import { Briefcase, Building2, ChevronDown, ChevronUp, MapPin, Search, X } from 'lucide-react';
import { Button } from '@/components/ui/forms/button';
import { ButtonProgressBar, LookupSearchPanel, SearchFilterCard } from '@/components/ui/design-system';
import { InputWithIcon } from '@/components/ui/forms/input-with-icon';
import { LocationAutosuggest } from '@/components/ui/forms/LocationAutosuggest';
import {
  useJobSearchHistoryStore,
  type JobSearchHistoryQuery,
} from '@/stores/useJobSearchHistoryStore';

interface JobsLookupSearchPanelProps {
  searchKeyword: string;
  onSearchKeywordChange: (value: string) => void;
  searchLocation: string;
  onSearchLocationChange: (value: string) => void;
  searchTitles: string[];
  titleInput: string;
  onTitleInputChange: (value: string) => void;
  onTitleInputKeyDown: KeyboardEventHandler<HTMLInputElement>;
  onAddTitle: () => void;
  onRemoveTitle: (title: string) => void;
  searchCompanies: string[];
  companyInput: string;
  onCompanyInputChange: (value: string) => void;
  onCompanyInputKeyDown: KeyboardEventHandler<HTMLInputElement>;
  onAddCompany: () => void;
  onRemoveCompany: (company: string) => void;
  onSearchSubmit: FormEventHandler<HTMLFormElement>;
  onApplySearchHistory: (query: JobSearchHistoryQuery) => void;
  onCancelSearch?: () => void;
  onClear: () => void;
  isSearching: boolean;
  searchProgress?: number;
  className?: string;
}

const formatCompanyDisplayValue = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) return trimmed;
  if (/^[A-Z\s]+$/.test(trimmed) && /[A-Z]/.test(trimmed)) {
    return trimmed.toLowerCase().replace(/\b[a-z]/g, (c) => c.toUpperCase());
  }
  return trimmed;
};

export function JobsLookupSearchPanel({
  searchKeyword,
  onSearchKeywordChange,
  searchLocation,
  onSearchLocationChange,
  searchTitles,
  titleInput,
  onTitleInputChange,
  onTitleInputKeyDown,
  onAddTitle,
  onRemoveTitle,
  searchCompanies,
  companyInput,
  onCompanyInputChange,
  onCompanyInputKeyDown,
  onAddCompany,
  onRemoveCompany,
  onSearchSubmit,
  onApplySearchHistory,
  onCancelSearch,
  onClear,
  isSearching,
  searchProgress = 0,
  className,
}: JobsLookupSearchPanelProps) {
  const [isHistoryExpanded, setIsHistoryExpanded] = useState(false);
  const historyEntries = useJobSearchHistoryStore((state) => state.entries);
  const addSearchToHistory = useJobSearchHistoryStore((state) => state.addSearch);
  const clearSearchHistory = useJobSearchHistoryStore((state) => state.clearHistory);
  const historyHydrated = useJobSearchHistoryStore((state) => state.hydrated);

  const hasSearchCriteria = Boolean(
    searchKeyword.trim() ||
    searchLocation.trim() ||
    searchTitles.length > 0 ||
    titleInput.trim() ||
    searchCompanies.length > 0 ||
    companyInput.trim()
  );

  const visibleHistoryEntries = isHistoryExpanded ? historyEntries : historyEntries.slice(0, 3);
  const shouldShowHistoryToggle = historyEntries.length > 3;

  const toUniqueList = (values: string[], pendingValue: string): string[] => {
    const deduped: string[] = [];
    const seen = new Set<string>();
    for (const item of [...values, pendingValue]) {
      const value = item.trim();
      if (!value) continue;
      const key = value.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      deduped.push(value);
    }
    return deduped;
  };

  const getCurrentSearchQuery = (): JobSearchHistoryQuery => {
    const titles = toUniqueList(searchTitles, titleInput);
    const companies = toUniqueList(searchCompanies, companyInput);
    return {
      title: searchKeyword.trim() || undefined,
      location: searchLocation.trim() || undefined,
      titles: titles.length > 0 ? titles : undefined,
      companies: companies.length > 0 ? companies : undefined,
    };
  };

  const handleSearchSubmit: FormEventHandler<HTMLFormElement> = (event) => {
    addSearchToHistory(getCurrentSearchQuery());
    onSearchSubmit(event);
  };

  const handleApplyHistorySearch = (query: JobSearchHistoryQuery) => {
    addSearchToHistory(query);
    onApplySearchHistory(query);
  };

  const truncateValue = (value: string, maxLength = 30): string =>
    value.length <= maxLength ? value : `${value.slice(0, maxLength - 3)}...`;

  const formatListValue = (values?: string[]): string | null => {
    if (!values || values.length === 0) return null;
    return values.length === 1 ? values[0] : `${values[0]} +${values.length - 1}`;
  };

  const getHistoryTagSegments = (query: JobSearchHistoryQuery): Array<{ icon: ComponentType<{ className?: string }>; text: string }> => {
    const segments: Array<{ icon: ComponentType<{ className?: string }>; text: string }> = [];
    const titlesValue = formatListValue(query.titles);
    const companiesValue = formatListValue(query.companies);

    if (query.title) {
      segments.push({ icon: Search, text: truncateValue(query.title) });
    } else if (titlesValue) {
      segments.push({ icon: Briefcase, text: truncateValue(titlesValue) });
    }
    if (query.location) {
      segments.push({ icon: MapPin, text: truncateValue(query.location) });
    }
    if (companiesValue) {
      segments.push({ icon: Building2, text: truncateValue(companiesValue) });
    }
    if (!query.title && titlesValue && segments.every((s) => s.icon !== Briefcase)) {
      segments.push({ icon: Briefcase, text: truncateValue(titlesValue) });
    }

    return segments.slice(0, 3);
  };

  return (
    <LookupSearchPanel onSubmit={handleSearchSubmit} className={className}>
      <div className="p-3 space-y-3 border-b border-border">
        <InputWithIcon
          icon={Search}
          placeholder="Job title..."
          value={searchKeyword}
          onChange={(event) => onSearchKeywordChange(event.target.value)}
          rightSlot={searchKeyword.trim() ? (
            <button
              type="button"
              onClick={() => onSearchKeywordChange('')}
              className="text-muted-foreground transition-colors hover:text-destructive"
              aria-label="Clear job title"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          ) : undefined}
        />
        <LocationAutosuggest
          value={searchLocation}
          onChange={onSearchLocationChange}
          placeholder="City, state, or 'Remote'"
          inputClassName="h-10 border text-sm font-medium"
        />
      </div>

      <div className="grid divide-y divide-border sm:grid-cols-2 sm:divide-x sm:divide-y-0">
        <SearchFilterCard
          className="p-3"
          title="Position Titles"
          placeholder="Add title (e.g. Engineering Manager)"
          values={searchTitles}
          inputValue={titleInput}
          onInputChange={onTitleInputChange}
          onInputKeyDown={onTitleInputKeyDown}
          onAdd={onAddTitle}
          onRemove={onRemoveTitle}
        />
        <SearchFilterCard
          className="p-3"
          title="Companies"
          placeholder="Add company name (e.g. Anthropic)"
          values={searchCompanies}
          inputValue={companyInput}
          onInputChange={onCompanyInputChange}
          onInputKeyDown={onCompanyInputKeyDown}
          onAdd={onAddCompany}
          onRemove={onRemoveCompany}
          displayValue={formatCompanyDisplayValue}
        />
      </div>

      {historyHydrated && historyEntries.length > 0 && (
        <div className="space-y-3 border-t border-border p-3">
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Search History
            </span>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={clearSearchHistory}
                className="text-xs font-semibold uppercase tracking-wide text-muted-foreground transition-colors hover:text-foreground"
              >
                Clear
              </button>
              {shouldShowHistoryToggle && (
                <button
                  type="button"
                  onClick={() => setIsHistoryExpanded((current) => !current)}
                  className="inline-flex items-center text-xs font-semibold uppercase tracking-wide text-muted-foreground transition-colors hover:text-foreground"
                >
                  {isHistoryExpanded ? (
                    <><ChevronUp className="mr-1 h-3 w-3" />Show less</>
                  ) : (
                    <><ChevronDown className="mr-1 h-3 w-3" />Show all</>
                  )}
                </button>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {visibleHistoryEntries.map((entry) => {
              const segments = getHistoryTagSegments(entry.query);
              return (
                <button
                  key={entry.id}
                  type="button"
                  title={entry.label}
                  onClick={() => handleApplyHistorySearch(entry.query)}
                  className="inline-flex max-w-full cursor-pointer items-center gap-1.5 rounded-sm border border-border/70 bg-background px-2 py-1 text-xs text-foreground transition-colors hover:border-primary/50 hover:bg-primary/5"
                >
                  {segments.map((segment, index) => (
                    <span key={`${entry.id}-${index}`} className="inline-flex min-w-0 items-center gap-1">
                      <segment.icon className="h-3 w-3 shrink-0 text-muted-foreground" />
                      <span className="max-w-[180px] truncate">{segment.text}</span>
                      {index < segments.length - 1 && (
                        <span className="text-muted-foreground/60">|</span>
                      )}
                    </span>
                  ))}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex gap-2 border-t border-border p-3">
        <Button
          type={isSearching ? 'button' : 'submit'}
          onClick={isSearching ? onCancelSearch : undefined}
          disabled={(!isSearching && !hasSearchCriteria) || (isSearching && !onCancelSearch)}
          className="border relative overflow-hidden"
        >
          {isSearching && onCancelSearch ? (
            <><X className="h-4 w-4 mr-2" />Stop search</>
          ) : isSearching ? (
            <><Search className="h-4 w-4 mr-2" />Searching...</>
          ) : (
            <><Search className="h-4 w-4 mr-2" />Search Jobs</>
          )}
          {isSearching && <ButtonProgressBar progress={searchProgress} />}
        </Button>
        <Button type="button" variant="outline" onClick={onClear} className="border">
          Clear
        </Button>
        <Link
          href="/search"
          className="ml-auto self-center text-xs text-muted-foreground underline underline-offset-2 transition-colors hover:text-primary"
        >
          Try Smart Search →
        </Link>
      </div>
    </LookupSearchPanel>
  );
}
